import { LoadingButton } from "@mui/lab";
import {
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	// RadioGroup,
	// FormControlLabel,
	// Radio,
} from "@mui/material";
import Button from "@mui/material/Button";
import {
	Contact,
	ContactType,
	useGetAllSuppliersLazyQuery,
	useGetMyContractorsLazyQuery,
} from "@repo/server-api";
import { OptionType } from "@repo/server-api/types";
import { useNotification, RuleComponent , PersonIdentification  } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect, useRef, useMemo } from "react";

import { emptyContact, validErrorState } from "@repo/legacy/components/helper/Index";

import { useCreateCompanyContact, useCreatePersonContact } from "../../hooks/ContactMutationHooks";
import CompanyIdentification from "../sections/CompanyIdentification";
// import ContactTypeSection from "../sections/ContactTypeSection";
import Dropdown from "../sections/Dropdown";

const DialogContact = (
	props: Readonly<{
		doNotShowClient?: boolean;
		open: boolean;
		setOpen: (value: boolean) => void;
		handleClose?: (value: boolean) => void;
		classes?: string;
		refreshData: (id: string) => void;
		type: ContactType | null;
		contact?: Contact | null;
		showContractorSelection?: boolean;
	}>,
) => {
	const { t } = useTranslation();

	const { showNotification } = useNotification();

	const open = useMemo(() => props.open, [props.open]);

	const [contact, setContact] = useState<Contact>({
		...emptyContact,
	});
	const [saveClicked, setSaveClicked] = useState<boolean>(false);
	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const contactTypeRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);
	const companyIdentificationRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);
	const personIdentificationRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);

	const [contactID, setContactID] = useState<string>("");

	const [createCompanyContact] = useCreateCompanyContact();
	const [createPersonContact] = useCreatePersonContact();

	const [type, setType] = useState<ContactType | null>(null);

	const [contractors, setContractors] = useState<Contact[]>([]);
	const [contractorId, setContractorId] = useState<string>("");

	const [getAllContractors] = useGetMyContractorsLazyQuery({
		fetchPolicy: "no-cache",
		variables: { first: 100 },
		onCompleted(data) {
			if (data.myContractors?.nodes) {
				setContractors(data.myContractors.nodes as unknown as Contact[]);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const [getAllSuppliers] = useGetAllSuppliersLazyQuery({
		fetchPolicy: "no-cache",
		variables: { first: 100 },
		onCompleted(data) {
			if (data.allContacts?.nodes) {
				setContractors(data.allContacts.nodes as unknown as Contact[]);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	const title = useMemo(() => {
		switch (props.type) {
			case ContactType.Contractor:
				setType(ContactType.Company);
				return t("contractor.new");
			case ContactType.Company:
				setType(ContactType.Company);
				return t("company.new");
			case ContactType.Freelancer:
				setType(ContactType.Person);
				return t("freelancer.new");
			case ContactType.Person:
				setType(ContactType.Person);
				return t("contact-person.new");
			case ContactType.Client:
				setType(ContactType.Company);
				return t("client.new");
			case ContactType.Supplier:
				setType(ContactType.Company);
				return t("supplier.new");
			default:
				return "";
		}
	}, [props.type]);

	const [selectedContractor, setSelectedContractor] = useState<OptionType | null>(null);

	const getContacts = (name: string) => {
		if (props.type === ContactType.Freelancer) {
			getAllSuppliers({
				variables: { first: 10, name: name },
			});
		} else {
			getAllContractors({
				variables: { first: 10, name: name },
			});
		}
	};

	useEffect(() => {
		if (open && props.showContractorSelection) {
			getContacts("");
		}
	}, [open]);

	useEffect(() => {
		if (props.contact && props.type && open && type) {
			const tmpContact = {
				...props.contact,
				contactTypes: [
					...props.contact.contactTypes.filter(
						(t) =>
							t !== ContactType.Company &&
							t !== ContactType.Person &&
							t !== props.type &&
							t !== type,
					),
					props.type,
					type,
				],
			};
			setContact({ ...tmpContact });
		} else if (props.type && open && type) {
			setContact({
				...emptyContact,
				contactTypes: [props.type, type],
			});
		}
	}, [type, props.type, props.contact, open]);

	const handleClose = () => {
		props.setOpen(false);
		if (props.handleClose) props.handleClose(false);
		setSaveClicked(false);
		setErrors({});
		setContactID("");
		setContact({ ...emptyContact });
		setType(null);
	};

	useEffect(() => {
		if (contactID) {
			props.refreshData(contactID);
			handleClose();
		}
	}, [contactID]);

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		localErrors.contactType = (await contactTypeRef.current?.validate()) ?? {};

		switch (type) {
			case ContactType.Person:
				localErrors.personIdentification =
					(await personIdentificationRef.current?.validate()) ?? {};
				break;
			case ContactType.Company:
				localErrors.companyIdentification =
					(await companyIdentificationRef.current?.validate()) ?? {};
				break;
			default:
				break;
		}

		if (props.showContractorSelection && !contractorId && type === ContactType.Person) {
			localErrors.contractorSelection =
				props.type === ContactType.Freelancer
					? t("supplier-is-required", { ns: "validation" })
					: t("contractor-is-required", { ns: "validation" });
		}

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();

		const isValid = await validateFields();

		if (isValid) {
			setSaveClicked(true);
			switch (type) {
				// case ContactType.Freelancer:
				case ContactType.Person: {
					createPersonContact(contact, contractorId)
						.then((res) => {
							if (res.id) {
								showNotification(t("contact.freelancerCreated"), "success");
								setContactID(res.id);
							} else if (res.errors) {
								showNotification(
									res.errors.cause.result.errors
										.map((e: { message: string }) => e.message)
										.join(" "),
									"error",
								);
							}
						})
						.catch((err) => {
							showNotification(err.message, "error");
						});
					break;
				}
				case ContactType.Company:
				case ContactType.Contractor:
					createCompanyContact(contact)
						.then((res) => {
							if (typeof res === "string") {
								showNotification(t("contact.companyCreated"), "success");
								setContactID(res);
							} else if (res.errors) {
								showNotification(
									res.errors.cause.result.errors
										.map((e: { message: string }) => e.message)
										.join(" "),
									"error",
								);
							}
						})
						.catch((err) => {
							showNotification(err.message, "error");
						});
					break;
				default: {
					break;
				}
			}
		}
	};

	return (
		<div>
			<Dialog
				open={open}
				PaperProps={{
					component: "form",
					onSubmit: handleSubmit,
				}}
				maxWidth="md"
				fullWidth={true}
				data-testid="contact-dialog">
				<DialogTitle>{title}</DialogTitle>
				<DialogContent className="flex-col gap-5">
					{/* <RuleComponent rule={() => props.type === ContactType.Client}>
						<FormControl>
							<RadioGroup
								value={type}
								name="radio-buttons-group"
								onChange={(e) => {
									setType(e.target.value as ContactType);
									setContact({
										...contact,
										contactTypes: [
											...(contact.contactTypes.filter(
												(t) =>
													t !== ContactType.Person && t !== ContactType.Company,
											) || []),
											e.target.value as ContactType,
										],
									});
								}}
								style={{ flexDirection: "row" }}
							>
								<FormControlLabel
									value={ContactType.Person}
									control={<Radio />}
									label={t("common.person")}
									className="left"
								/>
								<FormControlLabel
									value={ContactType.Company}
									control={<Radio />}
									label={t("common.company")}
									className="left"
								/>
							</RadioGroup>
						</FormControl>
					</RuleComponent> */}
					{/* <RuleComponent
						rule={() =>
							props.type !== ContactType.Contractor &&
							props.type !== ContactType.Client
						}
					>
						<ContactTypeSection
							doNotShowClient={!!props.doNotShowClient}
							ref={contactTypeRef}
							errors={
								errors?.contactType as Record<string, boolean | object | string>
							}
							setErrors={(value: Record<string, boolean | object | string>) =>
								setErrors &&
								setErrors({
									...errors,
									contactType:
										typeof errors.contactType === "object" &&
										errors.contactType !== null
											? { ...errors.contactType, ...value }
											: value,
								})
							}
							contact={contact}
							setContact={setContact}
						/>
					</RuleComponent> */}

					{/* <RuleComponent
						rule={() =>
							props.type === ContactType.Company ||
							props.type === ContactType.Contractor
						}
					> */}
					<RuleComponent rule={() => type === ContactType.Company}>
						<Box sx={{ width: "100%" }}>
							<CompanyIdentification
								isDialog={true}
								ref={companyIdentificationRef}
								setErrors={(value: Record<string, boolean | object | string>) =>
									setErrors &&
									setErrors({
										...errors,
										companyIdentification: {
											...(typeof errors?.companyIdentification === "object" &&
											errors?.companyIdentification !== null
												? errors.companyIdentification
												: {}),
											...value,
										},
									})
								}
								errors={errors?.companyIdentification as Record<string, boolean | object | string>}
								contact={contact}
								setContact={setContact}
							/>
						</Box>
					</RuleComponent>

					{/* <RuleComponent
						rule={() =>
							props.type === ContactType.Freelancer						}
					> */}
					<RuleComponent rule={() => type === ContactType.Person}>
						<Box sx={{ width: "100%" }}>
							<PersonIdentification
								isDialog={true}
								ref={personIdentificationRef}
								setErrors={(value: Record<string, boolean | object | string>) =>
									setErrors &&
									setErrors({
										...errors,
										personIdentification: {
											...(typeof errors?.personIdentification === "object" &&
											errors?.personIdentification !== null
												? errors.personIdentification
												: {}),
											...value,
										},
									})
								}
								errors={errors?.personIdentification as Record<string, boolean | object | string>}
								contact={contact}
								setContact={setContact}
							/>
						</Box>
					</RuleComponent>
					<RuleComponent rule={() => type === ContactType.Person}>
						<Box className="w-100">
							<Dropdown
								dropdownType="contact"
								doNotShowMenu={true}
								elements={contractors}
								element={{
									title: selectedContractor?.title || "",
									inputValue: selectedContractor?.inputValue || "",
									item: selectedContractor?.item || null,
								}}
								setElement={(e) => {
									setSelectedContractor(e);
									if (e?.item) {
										setContractorId(e.item.id);
									} else {
										setContractorId("");
									}
									setErrors({ ...errors, contractorSelection: "" });
								}}
								getContacts={getContacts}
								searchLabel={
									props.type === ContactType.Freelancer
										? t("select-supplier")
										: t("select-contractor")
								}
								searchPlaceholder={
									props.type === ContactType.Freelancer
										? t("select-supplier")
										: t("select-contractor")
								}
								error={errors.contractorSelection as string}
								setError={(value: string) => setErrors({ ...errors, contractorSelection: value })}
							/>
						</Box>
					</RuleComponent>
				</DialogContent>
				<DialogActions>
					<Button data-testid="close-button" variant="outlined" onClick={handleClose}>
						{t("cancel")}
					</Button>
					<LoadingButton
						loading={saveClicked}
						type="submit"
						variant="contained"
						data-testid="save-button"
						className={saveClicked ? "" : "blueButton"}>
						{t("save")}
					</LoadingButton>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default DialogContact;
